import { Box } from '@mui/material';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import * as appStyles from '../../../../AppStyle';
import ButtonGroup from '../../../../components/ButtonGroup';
import Authors from '../../../../components/FormComponent/Authors';
import Categorization from '../../../../components/FormComponent/Categorization';
import Content from '../../../../components/FormComponent/Content';
import * as contentStyles from '../../../../components/FormComponent/Content/styles';
import FilesComplementary from '../../../../components/FormComponent/FilesComplementary';
import MainFile from '../../../../components/FormComponent/MainFile';
import MetaTag from '../../../../components/FormComponent/MetaTag';
// Helpers
import toast from '../../../../helpers/toast';
import { useContentSubmit } from '../../../../hooks';
import { createContent, updateContent } from '../../../../services/contents';
import { getResearche } from '../../../../services/researches';
import * as styles from './styles';

const CreateAndEditResearch: React.FC<any> = ({ type }: any) => {
  const [searchParams] = useSearchParams();
  const [isEditing, setIsEditing] = React.useState(true);
  const [isLoading, setIsloading] = React.useState(true);
  const [researches, setResearches] = React.useState<any>();
  const [isShowing, setIsShowing] = React.useState(false);
  const onError = () => {
    Swal.fire({
      title: 'Por favor, preencha o formulário corretamente.',
    });
  };
  const navigate = useNavigate();

  const getContent = async () => {
    setIsloading(true);
    await getResearche(searchParams.get('id')).then(result => {
      setResearches(result);
      if (isEditing) {
        fillGroup(result);
        setValue('part', result.part);
        setValue('antecipation', result.antecipation);
        setValue('antecipation_days', result.antecipation_days);
      }
    });
    setIsloading(false);
  };

  React.useEffect(() => {
    if (searchParams.get('id') != null) {
      getContent();
      if (searchParams.get('view') != null) {
        setIsShowing(true);
      }
      setIsEditing(true);
    } else {
      setIsEditing(false);
      setIsloading(false);
    }
  }, []);

  const methods = useForm();
  const { handleSubmit, control, setValue, clearErrors, setError, getValues } =
    methods;

  function onSubmit(data: any, status: any) {
    data = { ...data, type: 'research', status: status };
    const id: string | null = searchParams.get('id');
    const formProps = { data };

    if (id !== null) formProps['id'] = id;

    const { obj } = useContentSubmit(formProps);

    if (!isEditing) {
      createContent(obj)
        .then(() => {
          Swal.fire({
            title: 'Pesquisa criado com sucesso',
            icon: 'success',
          });
          navigate(-1);
        })
        .catch(error => {
          if (error.response.data.errors) {
            Swal.fire({
              title: error.response.data.message,
              icon: 'error',
            });
            error.response.data.errors.map(el =>
              toast({
                message: el.message,
                type: 'warning',
              }),
            );
          } else {
            Swal.fire({
              title: 'Algo deu errado',
              icon: 'error',
            });
          }
        });
    } else {
      updateContent(obj)
        .then(() => {
          Swal.fire({
            title: 'Pesquisa alterado com sucesso',
            icon: 'success',
          });
          navigate(-1);
        })
        .catch(error => {
          if (error.response.data.errors) {
            Swal.fire({
              title: error.response.data.message,
              icon: 'error',
            });
            error.response.data.errors.map(el =>
              toast({
                message: el.message,
                type: 'warning',
              }),
            );
          } else {
            Swal.fire({
              title: 'Algo deu errado',
              icon: 'error',
            });
          }
        });
    }
  }

  const fillGroup = (date: any) => {
    const editingGroup = date.groups;
    const selectedGroup: Array<any> = [];
    const mappedTag = new Map();

    mappedTag.set('MINIMAL', 'Cliente - Cadastro Mínimo');
    mappedTag.set('PHYSICAL', 'Cliente - Pessoa Física');
    mappedTag.set('LEGAL', 'Cliente - Pessoa Jurídica');
    mappedTag.set('EMPLOYEE', 'Colaborador Sebrae');

    if (editingGroup) {
      editingGroup.forEach((tag: any) => {
        selectedGroup.push(mappedTag.get(tag));
      });
      setValue('antecipation_groups', editingGroup ? editingGroup : [null]);
    }
  };

  return (
    <>
      {!isLoading && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '900px',
            width: '100%',
            height: 'min-content',
          }}
        >
          <styles.CardInput>
            <FormProvider {...methods}>
              <Content
                getValues={getValues}
                clearErrors={clearErrors}
                setError={setError}
                control={control}
                setValue={setValue}
                content={researches}
                view={isShowing}
              >
                <contentStyles.ContentSeletor>
                  <Box
                    component="div"
                    sx={{
                      '& .MuiTextField-root': { m: 0, width: '100%' },
                    }}
                  >
                    <contentStyles.Label>Parte</contentStyles.Label>
                    <Controller
                      name="part"
                      control={control}
                      rules={{ required: true }}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <>
                          <contentStyles.InputText
                            name="part"
                            value={value}
                            onChange={(event: any) => {
                              onChange(event);
                              setValue('part', event.target.value);
                            }}
                            required
                            id="outlined-required"
                          />
                          {error !== undefined ? (
                            <appStyles.inputLabel
                              style={{ color: 'red', marginBottom: '0.5rem' }}
                            >
                              *Campo obrigatório
                            </appStyles.inputLabel>
                          ) : (
                            <></>
                          )}
                        </>
                      )}
                    />
                  </Box>
                </contentStyles.ContentSeletor>
              </Content>
            </FormProvider>
          </styles.CardInput>
          <styles.CardInput>
            <FilesComplementary
              control={control}
              setValue={setValue}
              content={researches}
              view={isShowing}
              permission="public"
            />
          </styles.CardInput>
          <styles.CardInput>
            <Authors
              clearErrors={clearErrors}
              setError={setError}
              control={control}
              setValue={setValue}
              content={researches}
              view={isShowing}
            />
          </styles.CardInput>
          <styles.CardInput>
            <Categorization
              control={control}
              setValue={setValue}
              content={researches}
              view={isShowing}
            />
          </styles.CardInput>
          <styles.CardInput>
            <MainFile
              getValues={getValues}
              titleCompany={'Arquivo'}
              setValue={setValue}
              control={control}
              content={researches}
              view={isShowing}
              permission="public"
            />
          </styles.CardInput>
          <styles.CardInput>
            <MetaTag
              control={control}
              setValue={setValue}
              content={researches}
              view={isShowing}
            />
          </styles.CardInput>

          <ButtonGroup
            view={isShowing}
            handleSubmit={handleSubmit(onSubmit, onError)}
            isExclusive
          />
        </div>
      )}
    </>
  );
};

export default CreateAndEditResearch;
